import clsx from 'clsx';
import { chipBoxClass, useFilterBarDensity } from './filterBarDensity';
import React from 'react';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgPlus from '@icons/plus.svg?react';
import { FilterConfig } from '../types';
import { getFilterKey, getFilterLabel } from './filterChipUtils';

interface Props {
  inactiveConfs: FilterConfig[];
  onAdd: (key: string) => void;
}

/**
 * "+ Přidat filtr" — an invisible, full-bleed searchable Dropdown (same mechanic as the select chips)
 * whose panel opens directly on click; selecting an option adds that filter. The visible affordance is
 * styled as an inactive (unused) filter chip, overlaid on top (pointer-events-none, so clicks fall
 * through to the Dropdown).
 */
export const AddFilterMenu: React.FC<Props> = ({ inactiveConfs, onAdd }) => {
  const { tr } = useTranslationLgs();

  // Read before the early return: it is a hook.
  const density = useFilterBarDensity();
  if (inactiveConfs.length === 0) return null;

  const options = inactiveConfs.map(c => ({ label: getFilterLabel(c), value: getFilterKey(c) }));

  return (
    <div className="group relative inline-flex items-center">
      <Dropdown
        value={null}
        options={options}
        optionLabel="label"
        optionValue="value"
        filter
        onChange={e => e.value && onAdd(e.value)}
        appendTo={typeof document !== 'undefined' ? document.body : undefined}
        className="!absolute !inset-0 z-0 !w-full !h-full !min-h-0 !opacity-0 cursor-pointer"
        panelClassName="!mt-1"
        data-testid="add-filter"
      />
      <div
        className={clsx(
          "pointer-events-none relative z-10 inline-flex items-center gap-1.5 rounded border border-solid border-content-brandMedium bg-content-negativePrimary font-['DM_Sans'] font-normal text-content-primary select-none group-hover:border-content-brand",
          chipBoxClass(density),
        )}
      >
        <span className="whitespace-nowrap">{tr('RingilDataTable.addFilter', 'Add filter')}</span>
        <SvgPlus className="w-3.5 h-3.5 text-content-brand" />
      </div>
    </div>
  );
};
